import React, { useState } from "react";
import { Compass, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";
import StatusBadge from "@/components/StatusBadge";
import ReadAloud from "@/components/ReadAloud";

const DIMS = ["Strategy", "Business Process", "Data", "AI / GenAI", "Automation", "Cybersecurity", "Governance", "Compliance", "Operations", "Business Value"];
const MODELS = [{ v: "claude-sonnet-4-6", l: "Claude Sonnet 4.6" }, { v: "gpt-5.4", l: "GPT-5.4" }, { v: "gemini-3.1-pro", l: "Gemini 3.1 Pro" }];

export default function Advisor() {
  const [problem, setProblem] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!problem.trim()) return;
    setLoading(true); setRes(null);
    try { const { data } = await api.post("/advisor", { problem, model }); setRes(data); }
    catch { setRes({ analysis: "Advisor unavailable. Please try again.", source: "SIMULATED", model }); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title="AI Advisor" subtitle="WHICH use case fits, WHY it matters, HOW to build it, UNDER WHAT CONTROLS, and HOW to prove it — across 10 advisory dimensions." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Compass className="h-4 w-4 text-accent" /> Describe your business problem</h3>
          <textarea data-testid="advisor-input" value={problem} onChange={(e) => setProblem(e.target.value)} rows={7}
            placeholder="e.g. Our claims team manually reviews thousands of documents, causing delays and errors…"
            className="w-full rounded-xl border border-border bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          <div className="mt-3 flex items-center gap-2">
            <select data-testid="advisor-model" value={model} onChange={(e) => setModel(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
              {MODELS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <button data-testid="advisor-run" onClick={run} disabled={loading} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2 font-semibold text-accent-foreground disabled:opacity-50">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Advise
            </button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-1.5">
            {DIMS.map((d) => <span key={d} className="rounded-lg border border-border bg-secondary/40 px-2 py-1 text-[11px]">{d}</span>)}
          </div>
        </Card>

        <Card className="lg:col-span-2" testId="advisor-result">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Advisory Assessment</h3>
            {res && <div className="flex items-center gap-2"><StatusBadge value={res.source} /><ReadAloud context={res.analysis} testId="ra-advisor" /></div>}
          </div>
          {loading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>}
          {!loading && !res && <p className="py-16 text-center text-sm text-muted-foreground">Enter a problem and click Advise to generate a governed assessment.</p>}
          {!loading && res && <div data-testid="advisor-text" className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{res.analysis}</div>}
        </Card>
      </div>
    </div>
  );
}
